import { Loader2, Pencil, RefreshCw, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ErrorAction,
  getErrorActions,
  getErrorMessage,
  shouldShowAutoRetryMessage,
  calculateTimeUntilRetry,
} from "../utils/errorActions";
import { IssueReportDialog } from "./IssueReportDialog";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";

interface ErrorActionButtonsProps {
  postId: string;
  connectionId: string;
  platform: string;
  errorCode: string;
  errorMessage?: string;
  errorTimestamp?: number;
  retryCount: number;
  retryable?: boolean;
  cancelled?: boolean;
  hasPublishedConnections?: boolean;
  // The account is connected again after an auth failure, so a retry can work.
  accountReconnected?: boolean;
  // A carousel posted as separate stories that stopped partway through.
  storiesAlreadyLive?: { live: number; total: number };
  onCancelRetry?: (postId: string, connectionId: string) => Promise<void>;
  onEditPost?: (postId: string) => void;
}

export function ErrorActionButtons({
  postId,
  connectionId,
  platform,
  errorCode,
  errorMessage,
  errorTimestamp,
  retryCount,
  retryable,
  cancelled,
  hasPublishedConnections,
  accountReconnected,
  storiesAlreadyLive,
  onCancelRetry,
  onEditPost,
}: ErrorActionButtonsProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  const actions = getErrorActions(
    errorCode,
    retryCount,
    cancelled,
    hasPublishedConnections,
    retryable,
    accountReconnected
  );
  const userFriendlyMessage = getErrorMessage(
    errorCode,
    errorMessage,
    platform,
    retryCount,
    retryable,
    accountReconnected
  );
  const showAutoRetry = shouldShowAutoRetryMessage(
    errorCode,
    retryCount,
    cancelled,
    retryable
  );
  const timeUntilRetry = calculateTimeUntilRetry({
    timestamp: errorTimestamp || 0,
    retryCount,
    retryable,
  });

  const handleEdit = () => {
    if (onEditPost) {
      onEditPost(postId);
    } else {
      navigate(`/posts/${postId}/edit`);
    }
  };

  const handleRetry = async () => {
    if (!currentUser) return;

    setIsRetrying(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/posts/${postId}/connections/${connectionId}/retry`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to retry");
      }

      toast({
        title: "Retrying",
        description: "Your post is being retried. Check back in a moment.",
      });
    } catch (error) {
      console.error("Error retrying:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to retry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCancel = async () => {
    if (onCancelRetry) {
      setIsCanceling(true);
      try {
        await onCancelRetry(postId, connectionId);
      } finally {
        setIsCanceling(false);
      }
    }
  };

  const handleReport = () => {
    setShowReportDialog(true);
  };

  const handleReconnect = () => {
    navigate("/settings");
  };

  const getActionHandler = (action: ErrorAction) => {
    switch (action.type) {
      case 'edit':
        return handleEdit;
      case 'retry':
        return handleRetry;
      case 'cancel':
        return handleCancel;
      case 'report':
        return handleReport;
      case 'reconnect':
        return handleReconnect;
      default:
        return () => {};
    }
  };

  const getActionIcon = (action: ErrorAction) => {
    switch (action.type) {
      case 'edit':
        return <Pencil className="h-3 w-3 mr-1" />;
      case 'retry':
        return isRetrying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />;
      case 'reconnect':
        return <RefreshCw className="h-3 w-3 mr-1" />;
      case 'cancel':
        return isCanceling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />;
      default:
        return null;
    }
  };

  const isActionDisabled = (action: ErrorAction) => {
    if (action.type === 'retry') return isRetrying;
    if (action.type === 'cancel') return isCanceling;
    return false;
  };

  return (
    <div className="space-y-2">
      {/* Error message */}
      <p className="text-xs text-red-600">{userFriendlyMessage}</p>

      {storiesAlreadyLive && storiesAlreadyLive.live > 0 && (
        <p className="text-xs text-gray-600">
          {storiesAlreadyLive.live} of {storiesAlreadyLive.total} stories are
          already live. Retrying continues with slide{" "}
          {storiesAlreadyLive.live + 1}, so they won't be posted twice.
        </p>
      )}

      {/* Auto-retry indicator */}
      {showAutoRetry && timeUntilRetry && (
        <p className="text-xs text-gray-500">
          Retrying in {timeUntilRetry}
        </p>
      )}

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {actions.map((action) => (
            <Button
              key={action.type}
              variant={action.variant === 'default' ? 'default' : 'outline'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={getActionHandler(action)}
              disabled={isActionDisabled(action)}
            >
              {getActionIcon(action)}
              {action.label}
            </Button>
          ))}
        </div>
      )}

      {/* Issue Report Dialog */}
      <IssueReportDialog
        isOpen={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        postId={postId}
        connectionId={connectionId}
        platform={platform}
        errorCode={errorCode}
        errorMessage={userFriendlyMessage}
      />
    </div>
  );
}
